/* global google */
import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    InputAdornment,
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    Autocomplete,
    Avatar,
    Divider,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { googleMapsConfig } from '../../firebase/config';
import { getAllTemplates, saveTemplate, deleteTemplate } from '../../firebase/firestore';
import EditTournamentResults from './EditTournamentResults';

const AddTournamentForm = ({
    open,
    onClose,
    onSubmit,
    newTournament,
    handleInputChange,
    handleOrganizerSelect,
    organizers,
    handleAddPrize,
    handlePrizeChange,
    handleRemovePrize,
    handleOpenOrganizerDialog,
    isEditing
}) => {
    const locationNameInputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [mapsError, setMapsError] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [templateName, setTemplateName] = useState('');

    // Pobierz szablony przy otwarciu formularza
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const templatesData = await getAllTemplates();
                setTemplates(templatesData);
                // Resetuj wybrany szablon i formularz tylko przy dodawaniu nowego turnieju
                if (!isEditing) {
                    setSelectedTemplate('');
                    // Resetuj formularz do domyślnych wartości
                    handleInputChange({
                        target: {
                            name: 'format',
                            value: 'Core Constructed'
                        }
                    });
                    handleInputChange({
                        target: {
                            name: 'structure',
                            value: ''
                        }
                    });
                    handleInputChange({
                        target: {
                            name: 'rank',
                            value: 'REGIONAL'
                        }
                    });
                    handleInputChange({
                        target: {
                            name: 'prizes',
                            value: []
                        }
                    });
                    handleInputChange({
                        target: {
                            name: 'customLocation',
                            value: null
                        }
                    });
                    handleInputChange({
                        target: {
                            name: 'duration',
                            value: 1
                        }
                    });
                }
            } catch (error) {
                console.error('Błąd podczas pobierania szablonów:', error);
            }
        };
        if (open) {
            fetchTemplates();
        }
    }, [open, isEditing]);

    // Obsługa wyboru szablonu
    const handleTemplateSelect = (event) => {
        const templateId = event.target.value;
        setSelectedTemplate(templateId);

        if (templateId) {
            const template = templates.find(t => t.id === templateId);
            if (template) {
                // Aktualizuj formularz danymi z szablonu
                Object.entries(template).forEach(([key, value]) => {
                    if (key !== 'id' && key !== 'createdAt' && key !== 'modifiedAt') {
                        handleInputChange({
                            target: {
                                name: key,
                                value: value
                            }
                        });
                    }
                });
            }
        } else {
            // Resetuj formularz do domyślnych wartości
            handleInputChange({
                target: {
                    name: 'format',
                    value: 'Core Constructed'
                }
            });
            handleInputChange({
                target: {
                    name: 'structure',
                    value: ''
                }
            });
            handleInputChange({
                target: {
                    name: 'rank',
                    value: 'REGIONAL'
                }
            });
            handleInputChange({
                target: {
                    name: 'prizes',
                    value: []
                }
            });
            handleInputChange({
                target: {
                    name: 'customLocation',
                    value: null
                }
            });
            handleInputChange({
                target: {
                    name: 'duration',
                    value: 1
                }
            });
        }
    };

    // Zapisz szablon
    const handleSaveTemplate = async () => {
        try {
            if (!templateName) {
                alert('Proszę podać nazwę szablonu');
                return;
            }

            const templateData = {
                name: templateName,
                format: newTournament.format,
                structure: newTournament.structure,
                rank: newTournament.rank,
                prizes: newTournament.prizes,
                customLocation: newTournament.customLocation,
                duration: newTournament.duration || 1
            };

            await saveTemplate(templateData);
            setTemplateName('');
            const templatesData = await getAllTemplates();
            setTemplates(templatesData);
            alert('Szablon został zapisany');
        } catch (error) {
            console.error('Błąd podczas zapisywania szablonu:', error);
            alert('Wystąpił błąd podczas zapisywania szablonu');
        }
    };

    // Usuń szablon
    const handleDeleteTemplate = async (templateId) => {
        try {
            if (window.confirm('Czy na pewno chcesz usunąć ten szablon?')) {
                await deleteTemplate(templateId);
                const templatesData = await getAllTemplates();
                setTemplates(templatesData);
                if (selectedTemplate === templateId) {
                    setSelectedTemplate('');
                }
                alert('Szablon został usunięty');
            }
        } catch (error) {
            console.error('Błąd podczas usuwania szablonu:', error);
            alert('Wystąpił błąd podczas usuwania szablonu');
        }
    };

    // Funkcja do czyszczenia autocomplete
    const cleanupAutocomplete = () => {
        if (autocompleteRef.current) {
            google.maps.event.clearInstanceListeners(autocompleteRef.current);
            autocompleteRef.current = null;
        }
    };

    // Załaduj Google Maps API
    useEffect(() => {
        if (!window.google) {
            window.REACT_APP_GOOGLE_MAPS_API_KEY = googleMapsConfig.apiKey;
            window.loadGoogleMapsScript();
        }

        // Cleanup przy odmontowaniu komponentu
        return () => {
            cleanupAutocomplete();
        };
    }, []);

    // Sprawdź czy Google Maps API jest załadowane
    useEffect(() => {
        const checkGoogleMapsLoaded = () => {
            if (window.google && window.google.maps && window.google.maps.places) {
                setMapsLoaded(true);
            } else {
                setTimeout(checkGoogleMapsLoaded, 100);
            }
        };
        checkGoogleMapsLoaded();

        // Nasłuchuj na błędy Google Maps
        window.gm_authFailure = () => {
            setMapsError('Błąd autoryzacji Google Maps API. Sprawdź konfigurację klucza API.');
        };
    }, []);

    // Inicjalizuj autocomplete gdy API jest gotowe lub gdy formularz jest otwierany
    useEffect(() => {
        if (mapsLoaded && locationNameInputRef.current && open) {
            // Wyczyść poprzednią instancję
            cleanupAutocomplete();

            try {
                autocompleteRef.current = new google.maps.places.Autocomplete(locationNameInputRef.current, {
                    types: ['establishment', 'geocode']
                });

                // Dodaj style do pac-container
                const style = document.createElement('style');
                style.textContent = `
                    .pac-container {
                        background-color: rgba(17, 24, 39, 0.95) !important;
                        border: 1px solid rgba(76, 29, 149, 0.2) !important;
                        border-radius: 0.5rem !important;
                        box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37) !important;
                        backdrop-filter: blur(20px) !important;
                        z-index: 9999 !important;
                    }
                    .pac-item {
                        color: white !important;
                        padding: 8px !important;
                        font-size: 14px !important;
                        border-top: 1px solid rgba(76, 29, 149, 0.2) !important;
                    }
                    .pac-item:hover {
                        background-color: rgba(76, 29, 149, 0.2) !important;
                    }
                    .pac-item-query {
                        color: rgb(196, 181, 253) !important;
                    }
                    .pac-icon {
                        filter: invert(1) !important;
                    }
                `;
                document.head.appendChild(style);

                autocompleteRef.current.addListener('place_changed', () => {
                    const place = autocompleteRef.current.getPlace();
                    if (!place.geometry) return;

                    handleInputChange({
                        target: {
                            name: 'customLocation',
                            value: {
                                name: place.name,
                                address: place.formatted_address,
                                coordinates: {
                                    lat: place.geometry.location.lat(),
                                    lng: place.geometry.location.lng()
                                }
                            }
                        }
                    });
                });
            } catch (error) {
                console.error('Błąd podczas inicjalizacji Google Places Autocomplete:', error);
            }
        }
    }, [mapsLoaded, locationNameInputRef.current, open]);

    const handleGoogleMapsLink = async (link) => {
        try {
            if (link.includes('maps.app.goo.gl')) {
                // Konwertuj link na format Google Maps URL API
                const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(link)}`;

                // Otwórz link w nowym oknie
                const newWindow = window.open(mapsUrl, '_blank');

                // Po otwarciu linku, użytkownik może skopiować współrzędne z adresu
                if (newWindow) {
                    // Dodaj nasłuchiwanie na zmianę URL w nowym oknie
                    const checkUrl = setInterval(() => {
                        try {
                            const currentUrl = newWindow.location.href;
                            const latMatch = currentUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                            if (latMatch) {
                                const [, lat, lng] = latMatch;
                                handleInputChange({
                                    target: {
                                        name: 'customLocation',
                                        value: {
                                            ...newTournament.customLocation,
                                            coordinates: {
                                                lat: parseFloat(lat),
                                                lng: parseFloat(lng)
                                            }
                                        }
                                    }
                                });
                                clearInterval(checkUrl);
                                newWindow.close();
                            }
                        } catch (error) {
                            // Ignoruj błędy dostępu do cross-origin
                        }
                    }, 1000);

                    // Zatrzymaj sprawdzanie po 30 sekundach
                    setTimeout(() => {
                        clearInterval(checkUrl);
                    }, 30000);
                }
            } else {
                // Sprawdź stary format linku (@lat,lng)
                const latMatch = link.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                if (latMatch) {
                    const [, lat, lng] = latMatch;
                    handleInputChange({
                        target: {
                            name: 'customLocation',
                            value: {
                                ...newTournament.customLocation,
                                coordinates: {
                                    lat: parseFloat(lat),
                                    lng: parseFloat(lng)
                                }
                            }
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Błąd podczas przetwarzania linku Google Maps:', error);
        }
    };

    const commonTextFieldStyles = {
        '& .MuiOutlinedInput-root': {
            color: 'white',
            backgroundColor: 'rgba(76, 29, 149, 0.1)',
            '& fieldset': {
                borderColor: 'rgb(55, 65, 81)',
            },
            '&:hover fieldset': {
                borderColor: 'rgb(76, 29, 149)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgb(196, 181, 253)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgb(196, 181, 253)',
            '&.Mui-focused': {
                color: 'rgb(196, 181, 253)',
            },
        },
    };

    // Upewnij się, że prizes jest zawsze tablicą
    const prizes = Array.isArray(newTournament?.prizes) ? newTournament.prizes : [];

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(prizes);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Aktualizuj kolejność w stanie
        const updatedPrizes = items.map((item, index) => ({
            ...item,
            order: index
        }));

        handleInputChange({
            target: {
                name: 'prizes',
                value: updatedPrizes
            }
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: 'linear-gradient(145deg, rgba(76, 29, 149, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    borderRadius: '1rem',
                    border: '1px solid rgba(76, 29, 149, 0.2)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(90deg, rgba(76, 29, 149, 0.2) 0%, rgba(76, 29, 149, 0.1) 100%)',
                color: 'white',
                borderBottom: '1px solid rgba(76, 29, 149, 0.2)',
                fontWeight: '600',
                padding: '1.25rem',
                backdropFilter: 'blur(10px)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        sx={{ mr: 1 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    {isEditing ? 'Edytuj Turniej' : 'Dodaj Nowy Turniej'}
                </Box>
            </DialogTitle>

            <DialogContent sx={{
                pt: 2,
                pb: 1,
                background: 'linear-gradient(180deg, rgba(76, 29, 149, 0.05) 0%, rgba(17, 24, 39, 0.95) 100%)'
            }}>
                <Grid container spacing={2}>
                    {/* Szablony */}
                    <Grid item xs={12}>
                        <Card sx={{
                            backgroundColor: 'rgba(76, 29, 149, 0.1)',
                            border: '1px solid rgba(76, 29, 149, 0.2)',
                            borderRadius: '0.75rem',
                            mb: 2
                        }}>
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                                    <FormControl fullWidth size="small" sx={{
                                        ...commonTextFieldStyles,
                                        minWidth: { sm: '300px' }
                                    }}>
                                        <InputLabel id="template-select-label">Wybierz szablon</InputLabel>
                                        <Select
                                            labelId="template-select-label"
                                            value={selectedTemplate}
                                            onChange={handleTemplateSelect}
                                            label="Wybierz szablon"
                                        >
                                            <MenuItem value="">
                                                <em>Brak szablonu</em>
                                            </MenuItem>
                                            {templates.map((template) => (
                                                <MenuItem key={template.id} value={template.id}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                        <span>{template.name}</span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteTemplate(template.id);
                                                            }}
                                                            sx={{
                                                                color: 'rgb(239, 68, 68)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        flex: { xs: '1 1 auto', sm: '0 0 auto' },
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: 'stretch'
                                    }}>
                                        <TextField
                                            size="small"
                                            label="Nazwa szablonu"
                                            value={templateName}
                                            onChange={(e) => setTemplateName(e.target.value)}
                                            sx={{
                                                ...commonTextFieldStyles,
                                                flex: { xs: '1 1 auto', sm: '0 0 200px' }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            onClick={handleSaveTemplate}
                                            sx={{
                                                backgroundColor: 'rgb(76, 29, 149)',
                                                '&:hover': {
                                                    backgroundColor: 'rgb(91, 33, 182)'
                                                },
                                                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            Zapisz szablon
                                        </Button>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Podstawowe informacje */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Podstawowe informacje
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Nazwa turnieju*"
                            name="name"
                            value={newTournament.name}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            label="Data*"
                            name="date"
                            type="date"
                            value={newTournament.date}
                            onChange={handleInputChange}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            label="Godzina"
                            name="time"
                            value={newTournament.time}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                        <TextField
                            fullWidth
                            label="Liczba dni"
                            name="duration"
                            type="number"
                            value={newTournament.duration || 1}
                            onChange={handleInputChange}
                            size="small"
                            InputProps={{
                                inputProps: { min: 1, max: 7 }
                            }}
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Miasto*"
                            name="city"
                            value={newTournament.city}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Cena"
                            name="price"
                            value={newTournament.price}
                            onChange={handleInputChange}
                            size="small"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">zł</InputAdornment>,
                            }}
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Szczegóły turnieju */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Szczegóły turnieju
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Format"
                            name="format"
                            value={newTournament.format}
                            onChange={handleInputChange}
                            size="small"
                            select
                            sx={commonTextFieldStyles}
                        >
                            <MenuItem value="Core Constructed">Core Constructed</MenuItem>
                            <MenuItem value="Sealed">Sealed</MenuItem>
                            <MenuItem value="Draft">Draft</MenuItem>
                            <MenuItem value="Pack Rush">Pack Rush</MenuItem>
                            <MenuItem value="Custom">Custom</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Ranga"
                            name="rank"
                            value={newTournament.rank}
                            onChange={handleInputChange}
                            size="small"
                            select
                            sx={commonTextFieldStyles}
                        >
                            <MenuItem value="LOCAL">Lokalny</MenuItem>
                            <MenuItem value="REGIONAL">Regionalny</MenuItem>
                            <MenuItem value="NATIONAL">Krajowy</MenuItem>
                            <MenuItem value="INTERNATIONAL">Międzynarodowy</MenuItem>
                            <MenuItem value="FOREIGN">Zagraniczny</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Struktura turnieju"
                            name="structure"
                            value={newTournament.structure}
                            onChange={handleInputChange}
                            size="small"
                            multiline
                            rows={2}
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Linki */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Linki
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Link do wydarzenia*"
                            name="url"
                            value={newTournament.url}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Link do melee.gg"
                            name="meleeLink"
                            value={newTournament.meleeLink}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Wyniki turnieju */}
                    {isEditing && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{
                                    color: 'rgb(196, 181, 253)',
                                    mb: 1,
                                    fontWeight: '500'
                                }}>
                                    Wyniki turnieju
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <EditTournamentResults
                                    tournament={newTournament}
                                    onResultsUpdated={handleInputChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                            </Grid>
                        </>
                    )}

                    {/* Organizator */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '500'
                            }}>
                                Organizator*
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleOpenOrganizerDialog}
                                sx={{
                                    color: 'rgb(196, 181, 253)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(76, 29, 149, 0.2)'
                                    }
                                }}
                            >
                                Dodaj nowego
                            </Button>
                        </Box>
                        <Autocomplete
                            options={organizers}
                            getOptionLabel={(option) => option.name}
                            value={newTournament.organizer}
                            onChange={handleOrganizerSelect}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="Wybierz organizatora"
                                    sx={commonTextFieldStyles}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{
                                            bgcolor: 'rgba(76, 29, 149, 0.2)',
                                            color: 'rgb(196, 181, 253)',
                                            mr: 2,
                                            width: 32,
                                            height: 32
                                        }}>
                                            {option.name.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography sx={{ color: 'white', fontSize: '0.875rem' }}>
                                                {option.name}
                                            </Typography>
                                            <Typography sx={{
                                                color: 'rgb(196, 181, 253)',
                                                fontSize: '0.75rem'
                                            }}>
                                                {option.address}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        />
                    </Grid>

                    {/* Nagrody */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '500'
                            }}>
                                Nagrody
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddPrize}
                                sx={{
                                    color: 'rgb(196, 181, 253)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(76, 29, 149, 0.2)'
                                    }
                                }}
                            >
                                Dodaj nagrodę
                            </Button>
                        </Box>
                        {prizes.length === 0 ? (
                            <Typography sx={{
                                color: 'rgb(196, 181, 253)',
                                fontStyle: 'italic',
                                fontSize: '0.875rem'
                            }}>
                                Brak dodanych nagród
                            </Typography>
                        ) : (
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="prizes">
                                    {(provided) => (
                                        <Box
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                        >
                                            {prizes.map((prize, index) => (
                                                <Draggable
                                                    key={index}
                                                    draggableId={`prize-${index}`}
                                                    index={index}
                                                >
                                                    {(provided) => (
                                                        <Card
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            sx={{
                                                                mb: 1,
                                                                backgroundColor: 'rgba(76, 29, 149, 0.1)',
                                                                border: '1px solid rgb(55, 65, 81)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(76, 29, 149, 0.15)',
                                                                }
                                                            }}
                                                        >
                                                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                                <Grid container spacing={1} alignItems="center">
                                                                    <Grid item xs={1}>
                                                                        <Box
                                                                            {...provided.dragHandleProps}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                color: 'rgb(196, 181, 253)',
                                                                                cursor: 'grab'
                                                                            }}
                                                                        >
                                                                            <DragIndicatorIcon />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={11} sm={3}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Miejsce"
                                                                            value={prize.position}
                                                                            onChange={(e) => handlePrizeChange(index, 'position', e.target.value)}
                                                                            size="small"
                                                                            sx={commonTextFieldStyles}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={7}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Opis nagrody"
                                                                            value={prize.description}
                                                                            onChange={(e) => handlePrizeChange(index, 'description', e.target.value)}
                                                                            size="small"
                                                                            multiline
                                                                            rows={3}
                                                                            sx={commonTextFieldStyles}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={1}>
                                                                        <IconButton
                                                                            onClick={() => handleRemovePrize(index)}
                                                                            size="small"
                                                                            sx={{
                                                                                color: 'rgb(239, 68, 68)',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Grid>
                                                                </Grid>
                                                            </CardContent>
                                                        </Card>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </Box>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Niestandardowa lokalizacja */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Niestandardowa lokalizacja (opcjonalnie)
                        </Typography>
                        <Typography variant="caption" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 2,
                            display: 'block'
                        }}>
                            Jeśli turniej odbywa się w innym miejscu niż lokalizacja organizatora, wypełnij poniższe pola
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Nazwa miejsca"
                            name="customLocation.name"
                            value={newTournament.customLocation?.name || ''}
                            inputRef={locationNameInputRef}
                            onChange={(e) => {
                                handleInputChange({
                                    target: {
                                        name: 'customLocation',
                                        value: {
                                            ...newTournament.customLocation,
                                            name: e.target.value
                                        }
                                    }
                                });
                            }}
                            size="small"
                            sx={commonTextFieldStyles}
                            error={!!mapsError}
                            helperText={mapsError || "Wpisz nazwę miejsca lub adres, wybierz z listy..."}
                            autoComplete="off"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Adres"
                            name="customLocation.address"
                            value={newTournament.customLocation?.address || ''}
                            onChange={(e) => {
                                handleInputChange({
                                    target: {
                                        name: 'customLocation',
                                        value: {
                                            ...newTournament.customLocation,
                                            address: e.target.value
                                        }
                                    }
                                });
                            }}
                            size="small"
                            sx={commonTextFieldStyles}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Szerokość geograficzna"
                            name="customLocation.coordinates.lat"
                            type="number"
                            value={newTournament.customLocation?.coordinates?.lat || ''}
                            onChange={(e) => {
                                handleInputChange({
                                    target: {
                                        name: 'customLocation',
                                        value: {
                                            ...newTournament.customLocation,
                                            coordinates: {
                                                ...newTournament.customLocation?.coordinates,
                                                lat: parseFloat(e.target.value)
                                            }
                                        }
                                    }
                                });
                            }}
                            size="small"
                            InputProps={{
                                inputProps: { step: "0.000001" }
                            }}
                            sx={commonTextFieldStyles}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Długość geograficzna"
                            name="customLocation.coordinates.lng"
                            type="number"
                            value={newTournament.customLocation?.coordinates?.lng || ''}
                            onChange={(e) => {
                                handleInputChange({
                                    target: {
                                        name: 'customLocation',
                                        value: {
                                            ...newTournament.customLocation,
                                            coordinates: {
                                                ...newTournament.customLocation?.coordinates,
                                                lng: parseFloat(e.target.value)
                                            }
                                        }
                                    }
                                });
                            }}
                            size="small"
                            InputProps={{
                                inputProps: { step: "0.000001" }
                            }}
                            sx={commonTextFieldStyles}
                            disabled
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{
                background: 'linear-gradient(0deg, rgba(76, 29, 149, 0.1) 0%, rgba(17, 24, 39, 0.95) 100%)',
                borderTop: '1px solid rgba(76, 29, 149, 0.2)',
                p: 2,
                backdropFilter: 'blur(10px)'
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'rgb(196, 181, 253)',
                        '&:hover': {
                            backgroundColor: 'rgba(76, 29, 149, 0.1)'
                        }
                    }}
                >
                    Anuluj
                </Button>
                <Button
                    onClick={onSubmit}
                    variant="contained"
                    sx={{
                        backgroundColor: 'rgb(76, 29, 149)',
                        '&:hover': {
                            backgroundColor: 'rgb(91, 33, 182)'
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(76, 29, 149, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                        }
                    }}
                    disabled={!newTournament.name || !newTournament.date || !newTournament.city || !newTournament.organizer_id}
                >
                    {isEditing ? 'Zapisz Zmiany' : 'Zapisz Turniej'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTournamentForm; 